import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, KeyRound, Lock, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Defined at module scope so React does not remount the <input> on every
// keystroke (which would cause the field to lose focus after each character).
const PasswordField = ({ id, label, value, onChange, show, setShow, testid, autoComplete }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <div className="relative">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
        placeholder="••••••••"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-700"
        aria-label={show ? 'Passwort verbergen' : 'Passwort anzeigen'}
        tabIndex={-1}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  </div>
);

const AdminAccount = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: 'Fehlende Angaben',
        description: 'Bitte alle Felder ausfüllen.',
        variant: 'destructive'
      });
      return;
    }
    if (newPassword.length < 8) {
      toast({
        title: 'Passwort zu kurz',
        description: 'Das neue Passwort muss mindestens 8 Zeichen lang sein.',
        variant: 'destructive'
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwörter stimmen nicht überein',
        description: 'Bitte prüfen Sie die Bestätigung des neuen Passworts.',
        variant: 'destructive'
      });
      return;
    }
    if (newPassword === currentPassword) {
      toast({
        title: 'Kein neues Passwort',
        description: 'Das neue Passwort muss sich vom aktuellen unterscheiden.',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const res = await authAPI.changePassword(currentPassword, newPassword);
      toast({
        title: 'Erledigt!',
        description: res.message || 'Passwort erfolgreich geändert.'
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        'Passwort konnte nicht geändert werden. Bitte erneut versuchen.';
      toast({
        title: 'Fehler',
        description: typeof detail === 'string' ? detail : 'Fehler beim Ändern des Passworts.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-account-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/upload')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
            data-testid="account-back-btn"
          >
            <ArrowLeft size={18} />
            Zurück zum Dashboard
          </button>
          <h1 className="text-3xl font-heading font-bold text-gray-900 flex items-center gap-3">
            <KeyRound size={28} />
            Konto & Passwort
          </h1>
          <p className="text-gray-600 mt-2">
            Eingeloggt als <span className="font-medium">{user?.email}</span>
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={20} className="text-gray-700" />
            <h2 className="text-xl font-heading font-semibold">Passwort ändern</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Verwenden Sie ein starkes Passwort mit mindestens 8 Zeichen.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <PasswordField
              id="current_password"
              label="Aktuelles Passwort"
              value={currentPassword}
              onChange={setCurrentPassword}
              show={showCurrent}
              setShow={setShowCurrent}
              testid="current-password-input"
              autoComplete="current-password"
            />
            <PasswordField
              id="new_password"
              label="Neues Passwort"
              value={newPassword}
              onChange={setNewPassword}
              show={showNew}
              setShow={setShowNew}
              testid="new-password-input"
              autoComplete="new-password"
            />
            <PasswordField
              id="confirm_password"
              label="Neues Passwort bestätigen"
              value={confirmPassword}
              onChange={setConfirmPassword}
              show={showConfirm}
              setShow={setShowConfirm}
              testid="confirm-password-input"
              autoComplete="new-password"
            />

            <div className="rounded-lg bg-blue-50 border border-blue-100 text-blue-900 text-sm p-4">
              <p className="font-semibold mb-1">Tipp für ein sicheres Passwort</p>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li>Mindestens 8 Zeichen (besser 12+)</li>
                <li>Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen mischen</li>
                <li>Keine leicht erratbaren Wörter (Name, Geburtsdatum)</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={saving}
              data-testid="change-password-submit"
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Passwort ändern
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminAccount;
